import { Button, Table } from 'semantic-ui-react'
import styled from 'styled-components'

// Re-export the shared tab pane so local imports (`./style`) keep working.
export { StyledMenuItem, StyledTabPane } from '../style'

const FileInput = styled.input`
  display: none;
`

// Two-column layout: form fields on the left, asset upload inline on the right,
// both stretched to the same height.
const LangRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;

  @media (max-width: 991px) {
    flex-direction: column;
  }
`

const LangFieldsCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const LangUploadCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;

  /* Make the file field stretch to fill the column height. */
  .field {
    display: flex;
    flex: 1;
    flex-direction: column;
  }
`

const Container = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 12px;
`

// Outer dashed frame matching the screenshot.
const Dropzone = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  border: 2px dashed #c8d2e0;
  border-radius: 16px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9aa7bd;
  }

  &.drag-over {
    border-color: #2bb673;
  }
`

// Inner gradient panel that holds the centered content.
const DropzoneInner = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 32px 24px;
  border-radius: 12px;
`

const IconBadge = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(45, 55, 72, 0.12);
  margin-bottom: 6px;

  i.icon {
    margin: 0;
    font-size: 1.8rem;
    line-height: 1;
  }
`

const Title = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
`

const Subtitle = styled.div`
  font-size: 0.95rem;
  color: #6b7280;
`

const SupportedTypes = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #aeb6c2;
  max-width: 560px;
`

const PickButton = styled(Button)`
  &&& {
    margin-top: 12px;
    background: #ffffff;
    color: #2d3748;
    font-weight: 600;
    border: 1px solid #e2e6ec;
    border-radius: 8px;
    padding: 11px 30px;
    box-shadow: 0 1px 2px rgba(45, 55, 72, 0.08);

    &:hover {
      background: #f7f9fc;
    }
  }
`

const PreviewCard = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fafbfc;
  border: 1px solid #e2e6ec;
  border-radius: 12px;
  overflow: hidden;
`

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background-color: #f3f6fb;
  border-bottom: 1px solid #e2e6ec;
  flex-wrap: wrap;

  .file-meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .file-name {
    font-weight: 600;
    color: #2d3748;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 60vw;
  }

  .file-size {
    font-size: 0.85rem;
    color: #8a94a6;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`

const PreviewBody = styled.div`
  width: 100%;
  flex: 1;
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  overflow: hidden;

  img,
  video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
  }

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`

export const AssetUpload = {
  LangRow,
  LangFieldsCol,
  LangUploadCol,
  FileInput,
  Container,
  Dropzone,
  DropzoneInner,
  IconBadge,
  Title,
  Subtitle,
  SupportedTypes,
  PickButton,
  PreviewCard,
  PreviewToolbar,
  PreviewBody
}

// ---- Master data (Stammdatenpflege) field tables ----

const DataTableSection = styled.div`
  margin-bottom: 28px;
`

const DataTable = styled(Table)`
  &&& {
    width: 100%;
    margin-bottom: 8px;

    th,
    td {
      padding: 8px 12px;
      font-size: 0.95rem;
      vertical-align: middle;
    }

    thead th {
      font-weight: 700;
      white-space: nowrap;
    }

    thead th.center {
      width: 20%;
    }

    tbody td.label-cell {
      font-weight: 500;
    }

    /* keep the checkbox compact and centered inside the cell */
    .field {
      margin: 0 !important;
      display: inline-flex;
    }
  }
`

export const DataFields = {
  DataTableSection,
  DataTable
}
